import React, { useCallback, useEffect, useState } from "react";
import { Mail, RefreshCw, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import SendCampaignDialog from "@/components/SendCampaignDialog";
import api from "@/lib/api";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}

export default function AdminSubscribersPage() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(0);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("subscribed");
  const [loading, setLoading] = useState(true);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = status === "all" ? {} : { status };
      const res = await api.get("/admin/subscribers", { params });
      setRows(res?.data?.subscribers || []);
      setActive(res?.data?.active_count || 0);
      setTotal(res?.data?.total_count || 0);
    } catch (_e) {
      toast.error("Could not load subscriber list.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const res = await api.get("/admin/campaigns");
      setCampaigns(res?.data?.campaigns || []);
    } catch (_e) {
      /* silent — non-fatal */
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const sentReleaseIds = campaigns
    .filter((c) => ["sent", "sending", "partial_failure"].includes(c.status))
    .map((c) => c.release_id);

  return (
    <>
      <GradientMesh />
      <div className="space-y-6 pb-10" data-testid="admin-subscribers-page">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Mail className="h-5 w-5 text-[color:var(--brand-primary)]" />
              Email Subscribers
            </span>
          }
          subtitle="Marketing / product-update opt-ins. Transactional email is not managed here."
          action={
            <div className="flex items-center gap-2">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  className="w-[180px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                  data-testid="admin-subscribers-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]">
                  <SelectItem value="subscribed">Active subscribers</SelectItem>
                  <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={refresh}
                disabled={loading}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="admin-subscribers-refresh"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                onClick={() => setCampaignOpen(true)}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="admin-subscribers-send-update"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Update Email
              </Button>
            </div>
          }
        />

        <div className="grid grid-cols-2 gap-3 max-w-md" data-testid="admin-subscribers-summary">
          <GlassCard className="p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
              Active subscribers
            </div>
            <div
              className="font-display text-2xl font-semibold text-[color:var(--text-1)] mt-1"
              data-testid="admin-subscribers-active-count"
            >
              {active}
            </div>
          </GlassCard>
          <GlassCard className="p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
              Total rows
            </div>
            <div
              className="font-display text-2xl font-semibold text-[color:var(--text-1)] mt-1"
              data-testid="admin-subscribers-total-count"
            >
              {total}
            </div>
          </GlassCard>
        </div>

        <GlassCard className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-[color:var(--text-3)] inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div
              className="p-6 text-sm text-[color:var(--text-3)]"
              data-testid="admin-subscribers-empty"
            >
              No subscribers match the current filter.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Opted-in</TableHead>
                  <TableHead>Opted-out</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.subscriber_id} data-testid={`admin-subscriber-row-${r.subscriber_id}`}>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell>
                      <span
                        className={
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold border " +
                          (r.marketing_email_opt_in
                            ? "border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]"
                            : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)]")
                        }
                      >
                        {r.marketing_email_opt_in ? "Subscribed" : "Unsubscribed"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-[color:var(--text-2)]">
                      {r.marketing_email_source || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-[color:var(--text-2)]">
                      {fmt(r.marketing_email_opted_in_at)}
                    </TableCell>
                    <TableCell className="text-xs text-[color:var(--text-2)]">
                      {fmt(r.marketing_email_opted_out_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </GlassCard>

        <div
          className="text-[11px] text-[color:var(--text-3)] leading-relaxed max-w-2xl"
          data-testid="admin-subscribers-footnote"
        >
          Sending a marketing campaign is intentionally a separate, deliberate
          action. Preview environments run in MOCKED mode and never contact
          SendGrid. Production sends require SENDGRID_MARKETING_ASM_GROUP_ID and
          POSTMORO_POSTAL_ADDRESS to be configured.
        </div>

        <GlassCard className="p-0 overflow-hidden" data-testid="admin-campaigns-history">
          <div className="px-4 py-3 border-b border-[color:var(--border-subtle)] flex items-center justify-between">
            <div className="font-display text-sm font-semibold text-[color:var(--text-1)]">Campaign history</div>
            <Button variant="outline" size="sm" onClick={loadCampaigns} disabled={campaignsLoading} className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="admin-campaigns-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${campaignsLoading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
          {campaignsLoading ? (
            <div className="p-6 text-sm text-[color:var(--text-3)] inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : campaigns.length === 0 ? (
            <div className="p-6 text-sm text-[color:var(--text-3)]" data-testid="admin-campaigns-empty">
              No campaigns yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Release</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.campaign_id} data-testid={`admin-campaign-row-${c.campaign_id}`}>
                    <TableCell className="text-xs text-[color:var(--text-2)]">{c.release_id}</TableCell>
                    <TableCell className="text-xs text-[color:var(--text-2)] max-w-[240px] truncate">{c.subject}</TableCell>
                    <TableCell className="text-xs text-[color:var(--text-2)]">{c.recipient_count} <span className="text-[color:var(--text-3)]">({c.success_count}/{c.failure_count})</span></TableCell>
                    <TableCell className="text-xs">
                      <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold border " + (
                        c.status === "sent" ? "border-[color:rgba(34,197,94,0.30)] bg-[color:rgba(34,197,94,0.06)] text-[color:var(--success)]" :
                        c.status === "partial_failure" ? "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] text-[color:var(--accent-warm)]" :
                        c.status === "failed" ? "border-[color:rgba(239,68,68,0.35)] bg-[color:rgba(239,68,68,0.06)] text-[color:var(--danger)]" :
                        "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)]"
                      )}>{c.status.replace("_"," ")}</span>
                    </TableCell>
                    <TableCell className="text-xs text-[color:var(--text-2)]">{fmt(c.sent_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </GlassCard>
      </div>
      <SendCampaignDialog
        open={campaignOpen}
        onOpenChange={setCampaignOpen}
        sentReleaseIds={sentReleaseIds}
        onSent={loadCampaigns}
      />
    </>
  );
}
